/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { HelperText, InputBase, Label } from "../..";
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { cursorNotAllowed, displayBlock, positionRelative } from "../../../styles/general";
import { useTheme } from "../../../theme";
import { classNames, onValidate, refType as ref } from "../../../utils";
const {
  spacing
} = useTheme();
const TextInput = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  autoComplete,
  autoFocus,
  autoWidth,
  className,
  defaultValue,
  disabled,
  endIcon,
  endIconProps,
  error,
  helperTextProps,
  hoverTooltip,
  inputProps,
  inputRef,
  inputStyle,
  label,
  labelProps,
  maxRows,
  multiline,
  nonStyle,
  onBlur,
  onChange,
  onFocus,
  onInput,
  onKeyDown,
  onKeyUp,
  onPaste,
  placeholder,
  readOnly,
  required,
  rows,
  startIcon,
  startIconProps,
  status,
  style,
  type,
  validates,
  value: valueProp,
  viewType,
  ...props
}, reference) => {
  const ref = useRef(null);
  if (!inputRef) {
    inputRef = useRef(null);
  }
  const [value, setValue] = useState(valueProp || defaultValue);
  const isError = !!error;
  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const validateResult = validates && onValidate(value, validates, true);
  return jsx("div", {
    ref: ref,
    css: TextInputRoot,
    className: classNames('DGN-UI-TextInput', className, disabled && 'disabled'),
    style: style,
    ...props
  }, !!label && jsx(Label, {
    required: required,
    disabled: disabled,
    ...labelProps
  }, label), jsx(InputBase, {
    type: /text|number|password/.test(type) ? type : 'text',
    autoComplete: autoComplete,
    inputRef: inputRef,
    viewType: viewType,
    defaultValue: defaultValue,
    placeholder: placeholder,
    autoFocus: autoFocus,
    disabled: disabled,
    readOnly: readOnly,
    multiline: multiline,
    autoWidth: autoWidth,
    rows: rows,
    maxRows: maxRows,
    status: isError || validateResult ? 'danger' : status,
    inputProps: inputProps,
    inputStyle: inputStyle,
    startIcon: startIcon,
    startIconProps: startIconProps,
    endIcon: endIcon,
    endIconProps: endIconProps,
    onChange: onChange,
    onBlur: onBlur,
    onFocus: onFocus,
    onInput: onInput,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    onPaste: onPaste,
    value: valueProp,
    hoverTooltip: hoverTooltip,
    nonStyle: nonStyle
  }), isError && jsx(HelperText, {
    ...helperTextProps,
    disabled: disabled,
    status: status
  }, error), !isError && validateResult && jsx(HelperText, {
    ...helperTextProps,
    disabled: disabled
  }, validateResult));
}));
const TextInputRoot = css`
    ${displayBlock};
    ${positionRelative};
    margin-bottom: ${spacing([5])};
    &.disabled {
        ${cursorNotAllowed};
    }
`;
TextInput.defaultProps = {
  autoComplete: 'off',
  autoFocus: false,
  autoWidth: false,
  className: '',
  defaultValue: '',
  disabled: false,
  hoverTooltip: false,
  error: '',
  label: '',
  multiline: false,
  readOnly: false,
  required: false,
  status: 'default',
  type: 'text',
  viewType: 'underlined'
};
TextInput.propTypes = {
  /** This prop helps users to fill forms faster, especially on mobile devices. The name can be confusing, as it's more like an autofill. You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill). */
  autoComplete: PropTypes.string,
  /** If `true`, the input element is focused during the first mount. */
  autoFocus: PropTypes.bool,
  /** If `true`, the width of the component will automatically be set according to the contents inside the input, don't run with multiline = true */
  autoWidth: PropTypes.bool,
  /** Class for component. */
  className: PropTypes.string,
  /** The default value. Use when the component is not controlled. */
  defaultValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** If `true`, the component is disabled.*/
  disabled: PropTypes.bool,
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed to the right of input. */
  endIcon: PropTypes.any,
  /** Props of end icon in InputBase component. */
  endIconProps: PropTypes.object,
  /** Error displayed below input. */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**  [Props](https://core.diginet.com.vn/ui/?path=/story/form-control-text-helpertext) of helper text. */
  helperTextProps: PropTypes.object,
  /** [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#attributes) applied to the input element. */
  inputProps: PropTypes.object,
  /** Pass a ref to the input element. */
  inputRef: ref,
  /** Style inline of input element. */
  inputStyle: PropTypes.object,
  /** The label of the component. */
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** [Props](https://core.diginet.com.vn/ui/?path=/docs/form-control-text-label--simple) of label. */
  labelProps: PropTypes.object,
  /** Maximum number of rows to display when multiline option is set to true. */
  maxRows: PropTypes.number,
  /** If `true`, a textarea element is rendered. */
  multiline: PropTypes.bool,
  /** Callback fired when the input is blurred. */
  onBlur: PropTypes.func,
  /** Callback fired when the value is changed. */
  onChange: PropTypes.func,
  /** Callback fired when the input is focused. */
  onFocus: PropTypes.func,
  /** Callback fired when input. */
  onInput: PropTypes.func,
  /** Callback fired when pressing a key. */
  onKeyDown: PropTypes.func,
  /** Callback fired when releases a key. */
  onKeyUp: PropTypes.func,
  /** The short hint displayed in the input before the user enters a value. */
  placeholder: PropTypes.string,
  /** If `true`, the component is readOnly. */
  readOnly: PropTypes.bool,
  /** If `true`, the input element is required. */
  required: PropTypes.bool,
  /** Number of rows to display when multiline option is set to true. */
  rows: PropTypes.number,
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed to the left of input. */
  startIcon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Props of start icon in InputBase component. */
  startIconProps: PropTypes.object,
  /** Status type to display color for input. */
  status: PropTypes.oneOf(['default', 'warning', 'success', 'danger']),
  /** Style inline of component. */
  style: PropTypes.object,
  /** Type of the input element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types). */
  type: PropTypes.oneOf(['inform', 'edit', 'text', 'number', 'password']),
  /** Validation value, argument can:<br/>
   * * string: the validation rule. Example required.<br/>
   * * object: the validation rule insist name, property, message. Example {name: 'min', compareValue: 9, message: 'Error'} or {max: 99}<br/>
   * * array: the validation rule list, insist object/string
   */
  validates: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array, PropTypes.func]),
  /** The value of the input element, required for a controlled component. */
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** The variant to use. */
  viewType: PropTypes.oneOf(['underlined', 'outlined', 'none'])
};
export default TextInput;